import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancerBasicInfoPage = () => {
  const [freelancerInfo, setFreelancerInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  const [searchId, setSearchId] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'Freelancer ID',
    'Name',
    'Email',
    'Phone',
    'State',
    'City',
    'Gender',
    'DOB',
    'Qualification',
    'Content Writing Experience',
    'Password',
    'Created At'
  ];

  useEffect(() => {
    const fetchFreelancerInfo = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(
          `${apiBaseURL}/api/freelancer-basic-info`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFreelancerInfo(response.data);
      } catch (err) {
        setError('Failed to fetch freelancer basic info.');
      } finally {
        setLoading(false);
      }
    };

    fetchFreelancerInfo();
  }, [apiBaseURL]);

  if (loading) return <div>Loading freelancer info...</div>;
  if (error) return <div>{error}</div>;

  const mappedData = freelancerInfo.map(freelancer => ({
    freelancer_id: freelancer.freelancer_id ?? '',
    name: freelancer.name ?? '',
    email: freelancer.email ?? '',
    phone: freelancer.phone ?? '',
    state: freelancer.state ?? '',
    city: freelancer.city ?? '',
    gender: freelancer.gender ?? '',
    dob: freelancer.date_of_birth ? new Date(freelancer.date_of_birth).toLocaleDateString() : '',
    qualification: freelancer.highest_qualification ?? '',
    content_writing_experience: freelancer.content_writing_experience ?? '',
    password: freelancer.password ?? '',
    created_at: freelancer.created_at ? new Date(freelancer.created_at).toLocaleString() : '',
    created_at_obj: freelancer.created_at ? new Date(freelancer.created_at) : null
  }));

  const uniqueStates = [...new Set(mappedData.map(item => item.state))].filter(Boolean);
  const uniqueCities = [...new Set(mappedData.map(item => item.city))].filter(Boolean);

  const filteredData = mappedData.filter(item => {
    const createdAtDate = item.created_at_obj;
    const isWithinRange =
      (!startDate || (createdAtDate && createdAtDate >= new Date(startDate))) &&
      (!endDate || (createdAtDate && createdAtDate <= new Date(endDate + 'T23:59:59')));

    return (
      item.freelancer_id.toString().includes(searchId) &&
      item.name.toLowerCase().includes(searchName.toLowerCase()) &&
      item.phone.toString().includes(searchPhone) &&
      (filterState ? item.state === filterState : true) &&
      (filterCity ? item.city === filterCity : true) &&
      isWithinRange
    );
  });

  const sortedData = [...filteredData].sort((a, b) => {
    const aId = Number(a.freelancer_id) || 0;
    const bId = Number(b.freelancer_id) || 0;
    return sortOrder === 'asc' ? aId - bId : bId - aId;
  });

  const toggleSortOrder = () => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));

  const handleDownloadExcel = () => {
    const now = new Date();
    const headerRows = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Basic Info'],
      [`Downloaded on: ${now.toLocaleString('en-IN')}`],
      [],
    ];
    const dataRows = sortedData.map(row =>
      columns.map(col => row[col.toLowerCase().replace(/ /g, '_')] || '')
    );

    const wsData = [...headerRows, columns, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Freelancer Info');

    const fileName = `Freelancer_Basic_Info_${now.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
       <thead>
  <tr className="bg-gray-200">
    {columns.map((col, index) => (
      <th key={index} className="px-4 py-2 text-left whitespace-nowrap">
        {col === 'Freelancer ID' ? (
          <div className="flex items-center">
            <span>{col}</span>
            <button
              onClick={toggleSortOrder}
              className="ml-2 text-sm text-blue-500 hover:underline"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        ) : col}
      </th>
    ))}
  </tr>
</thead>
<tbody>
  {data.length === 0 ? (
    <tr>
      <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500 whitespace-nowrap">
        No data available
      </td>
    </tr>
  ) : (
    data.map((row, rowIndex) => (
      <tr key={rowIndex} className="border-b">
        {columns.map((col, colIndex) => (
          <td key={colIndex} className="px-4 py-2 whitespace-nowrap">
            {row[col.toLowerCase().replace(/ /g, '_')] || ''}
          </td>
        ))}
      </tr>
    ))
  )}
</tbody>

      </table>
    </div>
  );

  return (
    <div className="flex-1 p-6 bg-gray-100 ml-[16rem]">
      <h2 className="text-2xl font-semibold mb-4">Freelancer Basic Info</h2>

      <div className="mb-2 text-gray-700">
        Total Freelancers: <span className="font-bold">{mappedData.length}</span> | Filtered: <span className="font-bold">{filteredData.length}</span>
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        <input
          type="text"
          placeholder="Search by Freelancer ID"
          value={searchId}
          onChange={e => setSearchId(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        />
        <input
          type="text"
          placeholder="Search by Name"
          value={searchName}
          onChange={e => setSearchName(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        />
        <input
          type="text"
          placeholder="Search by Phone"
          value={searchPhone}
          onChange={e => setSearchPhone(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        />
        <select
          value={filterState}
          onChange={e => setFilterState(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">All States</option>
          {uniqueStates.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
        <select
          value={filterCity}
          onChange={e => setFilterCity(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">All Cities</option>
          {uniqueCities.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        />
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      <div className="mb-4">
        <button
          onClick={handleDownloadExcel}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          Download Excel
        </button>
      </div>

      <Table columns={columns} data={sortedData} />
    </div>
  );
};

export default FreelancerBasicInfoPage;
